/**
 * Password Validation (Gate F9)
 * 
 * Client-side password validation that mirrors the backend validatePasswordPolicy() logic.
 * Ensures consistent password requirements across the application.
 */

export interface PasswordRequirements {
  minLength: boolean;      // 12+ chars
  hasUppercase: boolean;   // A-Z
  hasLowercase: boolean;   // a-z
  hasDigit: boolean;       // 0-9
  hasSpecial: boolean;     // Special characters
}

export interface PasswordValidationResult {
  isValid: boolean;
  requirements: PasswordRequirements;
}

const MIN_PASSWORD_LENGTH = 12;
const UPPERCASE_REGEX = /[A-Z]/;
const LOWERCASE_REGEX = /[a-z]/;
const DIGIT_REGEX = /[0-9]/;
const SPECIAL_REGEX = /[!@#$%^&*()_+\-=\[\]{}|;:',.<>?\/~`\\]/;

export function validatePassword(password: string): PasswordValidationResult {
  const requirements: PasswordRequirements = {
    minLength: password.length >= MIN_PASSWORD_LENGTH,
    hasUppercase: UPPERCASE_REGEX.test(password),
    hasLowercase: LOWERCASE_REGEX.test(password),
    hasDigit: DIGIT_REGEX.test(password),
    hasSpecial: SPECIAL_REGEX.test(password),
  };

  const isValid = Object.values(requirements).every(Boolean);

  return {
    isValid,
    requirements,
  };
}

export const PASSWORD_REQUIREMENT_LABELS: Record<keyof PasswordRequirements, string> = {
  minLength: "At least 12 characters",
  hasUppercase: "At least one uppercase letter (A-Z)",
  hasLowercase: "At least one lowercase letter (a-z)",
  hasDigit: "At least one number (0-9)",
  hasSpecial: "At least one special character (!@#$%^&*...)",
};
